import React from 'react';
import { Box, Typography } from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { FEATURED_ARTISTS, isPackPromoActive } from '../data/artistContent';
import { ACCENT_COLOR } from '../config/uiTheme';

/**
 * PackCard - a backing tracks pack: image, name, description, artist,
 * price (with active promo), YouTube link and unlock button.
 */
const PackCard = ({ pack, isPurchased, onUnlock, onOpen, onWatch, compact = false }) => {
  const artist = FEATURED_ARTISTS[pack.artistId];
  const promoActive = isPackPromoActive(pack);
  // isPurchased est deja resolu par le parent (bundled -> full_app, IAP -> achat pack).
  const unlocked = isPurchased;

  return (
    <Box
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      data-jam-focus={`pack-${pack.id}`}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onOpen?.();
        }
      }}
      onClick={onOpen}
      sx={{
        backgroundColor: 'rgba(30, 30, 40, 0.6)',
        borderRadius: '16px',
        overflow: 'hidden',
        mb: 2,
        cursor: onOpen ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        ...(onOpen && { '&:hover': { backgroundColor: 'rgba(40, 40, 55, 0.7)' } })
      }}>
      {/* Pack image - 16:9, accent gradient fallback */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '42%',
        background: pack.imageUrl
          ? undefined
          : `linear-gradient(135deg, ${ACCENT_COLOR}55 0%, rgba(30,30,40,0.9) 70%)`,
        ...(pack.imageUrl && {
          backgroundImage: `url(${pack.imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        })
      }}>
        {/* Price badge */}
        <Box sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          px: 1.5,
          py: 0.5,
          borderRadius: '999px'
        }}>
          {pack.bundled ? (
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'white' }}>
              {unlocked ? 'Included' : 'Included with app'}
            </Typography>
          ) : (
            <>
              {promoActive && (
                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through' }}>
                  {pack.price}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: promoActive ? '#10b981' : 'white' }}>
                {promoActive ? pack.promoPrice : pack.price}
              </Typography>
            </>
          )}
        </Box>
        {promoActive && (
          <Box sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            backgroundColor: '#10b981',
            px: 1,
            py: 0.25,
            borderRadius: '6px'
          }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Promo until {new Date(pack.promoUntil).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Info */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {artist && (
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${ACCENT_COLOR} 0%, ${ACCENT_COLOR}99 100%)`,
              ...(artist.avatar && {
                backgroundImage: `url(${artist.avatar})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              })
            }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 'bold', color: 'white', lineHeight: 1.2 }}>
              {pack.name}
            </Typography>
            {artist && (
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                {artist.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Fixed 8-line slot (the Fretboard Fighters reference height) so every
            card in the horizontal row is the same height; longer text ellipses. */}
        <Typography sx={{
          fontSize: '0.85rem',
          color: 'rgba(255,255,255,0.65)',
          lineHeight: 1.45,
          mb: 1.5,
          height: `calc(${compact ? 3 : 8} * 1.45 * 0.85rem)`,
          display: '-webkit-box',
          WebkitLineClamp: compact ? 3 : 8,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {pack.description || ''}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {unlocked ? (
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: '999px',
              backgroundColor: 'rgba(16,185,129,0.15)'
            }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#10b981' }}>
                Unlocked
              </Typography>
            </Box>
          ) : (
            <Box
              onClick={(e) => { e.stopPropagation(); if (onUnlock) onUnlock(); }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2.5,
                py: 1,
                borderRadius: '999px',
                cursor: 'pointer',
                backgroundColor: 'var(--button)',
                transition: 'all 0.2s ease',
                '&:hover': { backgroundColor: 'var(--button-hover)' }
              }}
            >
              <LockOpenIcon sx={{ fontSize: 18, color: 'var(--button-text)' }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--button-text)' }}>
                {pack.bundled ? 'Unlock the app' : `Unlock ${promoActive ? pack.promoPrice : pack.price}`}
              </Typography>
            </Box>
          )}

          {pack.videoUrl && (
            <Box
              onClick={(e) => { e.stopPropagation(); onWatch ? onWatch() : window.open(pack.videoUrl, '_blank', 'noopener'); }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.75,
                borderRadius: '999px',
                cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.08)',
                transition: 'all 0.2s ease',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' }
              }}
            >
              <PlayCircleOutlineIcon sx={{ fontSize: 18, color: 'white' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'white' }}>
                Watch
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PackCard;
